<script lang="ts">
  import Button from '$components/reusable/Button.svelte';
  import Notification from '$components/reusable/Notification.svelte';
  import TextInput from '$components/reusable/TextInput.svelte';
  import Chevron from '$icons/Chevron.svelte';
  import DeleteIcon from '$icons/DeleteIcon.svelte';
  import LuggageIcon from '$icons/LuggageIcon.svelte';
  import GithubLogo from '$icons/logos/GithubLogo.svelte';
  import LinkedInLogo from '$icons/logos/LinkedInLogo.svelte';
  import TelegramLogo from '$icons/logos/TelegramLogo.svelte';
  import XLogo from '$icons/logos/XLogo.svelte';
  import type { editProfileSchema } from '$shared/lib';
  import { clickOutside } from '$utils';
  import { fade, slide } from 'svelte/transition';
  import type { Infer, SuperForm } from 'sveltekit-superforms/client';

  export let superform: SuperForm<Infer<typeof editProfileSchema>>;
  const { form, errors, enhance } = superform;

  const socialLinks = [
    {
      name: 'GitHub',
      icon: GithubLogo,
    },
    {
      name: 'Telegram',
      icon: TelegramLogo,
    },
    {
      name: 'X',
      icon: XLogo,
    },
    {
      name: 'LinkedIn',
      icon: LinkedInLogo,
    },
  ];
  let selectedSocialLinks: string[] = [];
  const removeSocialLink = (link: 'GitHub' | 'Telegram' | 'X' | 'LinkedIn') => {
    switch (link) {
      case 'GitHub':
        $form.githubHandle = null;
        break;
      case 'Telegram':
        $form.telegramHandle = null;
        break;
      case 'X':
        $form.twitterHandle = null;
        break;
      case 'LinkedIn':
        $form.linkedinHandle = null;
        break;
    }
    selectedSocialLinks = selectedSocialLinks.filter(
      (selectedSocialLink) => selectedSocialLink !== link,
    );
  };
  let showSocialSelection = false;
  $: {
    if ($form.githubHandle) {
      selectedSocialLinks = selectedSocialLinks.concat('GitHub');
    }
    if ($form.telegramHandle) {
      selectedSocialLinks = selectedSocialLinks.concat('Telegram');
    }
    if ($form.linkedinHandle) {
      selectedSocialLinks = selectedSocialLinks.concat('LinkedIn');
    }
    if ($form.twitterHandle) {
      selectedSocialLinks = selectedSocialLinks.concat('X');
    }
  }
  $: unselectedSocialLinks = socialLinks.filter((link) => !selectedSocialLinks.includes(link.name));
</script>

<form method="POST" use:enhance class="space-y-2xl p-[3px]">
  <!-- <TextInput label="Github" bind:value={user.githubHandle}>
		<GithubLogo slot="left-icon" class="h-4 w-4" />
	</TextInput>
	<TextInput label="Telegram" bind:value={user.telegramHandle}>
		<TelegramLogo slot="left-icon" class="h-4 w-4" />
	</TextInput>
	<TextInput label="X" bind:value={user.twitterHandle}>
		<XLogo slot="left-icon" class="h-4 w-4" />
	</TextInput>
	<TextInput label="LinkedIn" bind:value={user.linkedInHandle}>
		<LinkedInLogo slot="left-icon" class="h-4 w-4" />
	</TextInput> -->

  <div class="mt-lg flex items-center justify-between">
    <div class="flex items-end justify-start gap-x-2">
      <h2 class="text-[18px]">Social links</h2>
    </div>
    <div class="relative" use:clickOutside={() => (showSocialSelection = false)}>
      <Button
        secondary
        disabled={unselectedSocialLinks.length == 0}
        on:click={() => (showSocialSelection = !showSocialSelection)}
      >
        <span>Add</span>
        <Chevron class="!h-3 !w-3" />
      </Button>
      {#if showSocialSelection}
        <div
          transition:slide
          class="bg-gray-dark-3 py-2xs absolute right-0 top-10 z-10 min-h-fit w-fit overflow-y-scroll rounded"
        >
          {#if unselectedSocialLinks.length > 0}
            {#each unselectedSocialLinks as link}
              <button
                on:click={() => {
                  selectedSocialLinks = selectedSocialLinks.concat(link.name);
                  showSocialSelection = false;
                }}
                class="px-lg text-gray-dark-12 hover:bg-gray-dark-2 flex w-full items-center gap-x-2 py-2 text-sm leading-5"
              >
                <svelte:component this={link.icon} class="h-4 w-4" />

                <span class="whitespace-nowrap">{link.name}</span>
              </button>
            {/each}
          {/if}
        </div>
      {/if}
    </div>
  </div>
  {#if selectedSocialLinks.length == 0}
    <Notification
      title="You haven't added your social links yet."
      class="w-full"
      icon={LuggageIcon}
    />
  {/if}

  {#if selectedSocialLinks.includes('GitHub')}
    <div class="flex w-full items-start gap-x-4" transition:fade>
      <TextInput
        label="Github Username"
        bind:value={$form.githubHandle}
        bind:errors={$errors.githubHandle}
      >
        <GithubLogo slot="left-icon" class="h-4 w-4" />
      </TextInput>
      <Button tertiary icon size="md" class="mt-6" on:click={() => removeSocialLink('GitHub')}>
        <DeleteIcon />
      </Button>
    </div>
  {/if}
  {#if selectedSocialLinks.includes('Telegram')}
    <div class="flex w-full items-start gap-x-4" transition:fade>
      <TextInput
        label="Telegram Username"
        bind:value={$form.telegramHandle}
        bind:errors={$errors.telegramHandle}
      >
        <TelegramLogo slot="left-icon" class="h-4 w-4" />
      </TextInput>
      <Button tertiary icon size="md" class="mt-6" on:click={() => removeSocialLink('Telegram')}>
        <DeleteIcon />
      </Button>
    </div>
  {/if}
  {#if selectedSocialLinks.includes('X')}
    <div class="flex w-full items-start gap-x-4" transition:fade>
      <TextInput
        label="X Username"
        bind:value={$form.twitterHandle}
        bind:errors={$errors.twitterHandle}
      >
        <XLogo slot="left-icon" class="h-4 w-4" />
      </TextInput>
      <Button tertiary icon size="md" class="mt-6" on:click={() => removeSocialLink('X')}>
        <DeleteIcon />
      </Button>
    </div>
  {/if}
  {#if selectedSocialLinks.includes('LinkedIn')}
    <div class="flex w-full items-start gap-x-4" transition:fade>
      <TextInput
        label="LinkedIn Profile URL"
        bind:value={$form.linkedinHandle}
        bind:errors={$errors.linkedinHandle}
      >
        <LinkedInLogo slot="left-icon" class="h-4 w-4" />
      </TextInput>
      <Button tertiary icon size="md" class="mt-6" on:click={() => removeSocialLink('LinkedIn')}>
        <DeleteIcon />
      </Button>
    </div>
  {/if}
</form>
